import React, { useState } from 'react';
import { Gift, X } from 'lucide-react';
import { useT } from '../../context/LanguageContext';

/* "Join our rewards" on the QR menu — the guest's own door into the loyalty
   programme. Submits a PENDING lead to the public join endpoint (never an
   account: staff confirm at the counter, where the phone number is matched to
   the guest's existing record). Rendered only when the menu context says
   sign-ups are open, and not again on a phone that already sent one. */

const API_URL = process.env.REACT_APP_API_URL || '/api';
const JOINED_KEY = 'rewardsJoinSent';

const readJoined = () => {
  try { return localStorage.getItem(JOINED_KEY) === '1'; } catch { return false; }
};
const writeJoined = () => {
  try { localStorage.setItem(JOINED_KEY, '1'); } catch { /* private mode */ }
};

const JoinRewards = ({ programName, locationId, accent = '#EA580C', defaultPhone = '' }) => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [hidden] = useState(readJoined);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState(defaultPhone || '');
  const [email, setEmail] = useState('');
  const [birth, setBirth] = useState('');
  const [marketing, setMarketing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  if (hidden && !done) return null;

  const join = async () => {
    setErr('');
    if (!firstName.trim()) { setErr(t('guest.rewards.needName')); return; }
    if (phone.replace(/\D/g, '').length < 7) { setErr(t('guest.rewards.needPhone')); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/loyalty/public/join/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phone.trim(),
          email: email.trim(),
          birth_date: birth || undefined,
          accepts_marketing: marketing,
          source: 'QR',
          location_id: locationId || undefined,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || t('guest.rewards.failed'));
      }
      writeJoined();
      setDone(true);
    } catch (e) {
      setErr(e.message || t('guest.rewards.failed'));
    } finally {
      setSaving(false);
    }
  };

  const title = programName
    ? t('guest.rewards.cardTitleNamed', { name: programName })
    : t('guest.rewards.cardTitle');

  return (
    <>
      {/* The card above the footer — one line of pitch, one button. */}
      <div className="mt-10 rounded-2xl border border-orange-100 bg-orange-50/60 p-4 text-center">
        <div className="mx-auto h-9 w-9 rounded-full flex items-center justify-center"
          style={{ background: accent }}>
          <Gift size={18} className="text-white" />
        </div>
        {done ? (
          <>
            <p className="mt-2 font-semibold text-gray-900">{t('guest.rewards.doneTitle')}</p>
            <p className="mt-1 text-xs text-gray-500">{t('guest.rewards.doneBody')}</p>
          </>
        ) : (
          <>
            <p className="mt-2 font-semibold text-gray-900">{title}</p>
            <p className="mt-1 text-xs text-gray-500">{t('guest.rewards.cardBody')}</p>
            <button type="button" onClick={() => setOpen(true)}
              className="mt-3 px-5 py-2 rounded-xl text-white text-sm font-semibold"
              style={{ background: accent }}>
              {t('guest.rewards.cardCta')}
            </button>
          </>
        )}
      </div>

      {/* The form, as a bottom sheet like the rest of the guest page. */}
      {open && !done && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) setOpen(false); }}>
          <div className="w-full max-w-[440px] bg-white rounded-t-3xl p-5 pb-7 max-h-[88vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3">
              <p className="text-lg font-bold text-gray-900">{title}</p>
              {!saving && (
                <button type="button" onClick={() => setOpen(false)}
                  className="text-gray-400 hover:text-gray-600" aria-label="Close">
                  <X size={22} />
                </button>
              )}
            </div>
            <div className="mt-3 space-y-2.5">
              <div className="grid grid-cols-2 gap-2.5">
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t('guest.rewards.firstName')} autoComplete="given-name"
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm" />
                <input value={lastName} onChange={(e) => setLastName(e.target.value)}
                  placeholder={t('guest.rewards.lastName')} autoComplete="family-name"
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm" />
              </div>
              <input value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder={t('guest.rewards.phone')} inputMode="tel" autoComplete="tel"
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm" />
              <input value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={t('guest.rewards.email')} inputMode="email" autoComplete="email"
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm" />
              <div>
                <label className="text-xs text-gray-500">{t('guest.rewards.birthday')}</label>
                <input type="date" value={birth} onChange={(e) => setBirth(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                  className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-700" />
              </div>
              <label className="flex items-start gap-2 text-xs text-gray-600">
                <input type="checkbox" checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)} className="mt-0.5" />
                {t('guest.rewards.marketing')}
              </label>
              {err && <p className="text-xs text-red-600">{err}</p>}
              <button type="button" onClick={join} disabled={saving}
                className="w-full py-3 rounded-xl text-white text-sm font-semibold disabled:opacity-60"
                style={{ background: accent }}>
                {saving ? t('guest.rewards.joining') : t('guest.rewards.join')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default JoinRewards;
